import React, { useState } from 'react'
import {
  ShieldCheck,
  TrendingUp,
  Building2,
  ArrowRight,
  Lock,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { resolveImageUrl } from '@/utils/imageUrl'
import type { CustomLandingPageProps } from '../registry/CustomLandingRegistry'

export const BpexCustomLanding: React.FC<CustomLandingPageProps> = ({
  branding,
  loginUrl,
  contactEmail,
  contactPhone,
  onOpenDemoModal,
}) => {
  const [logoError, setLogoError] = useState<boolean>(false)
  const brandName = branding?.brand_name || 'BPEX Gestão Estratégica'
  const resolvedLogoUrl = resolveImageUrl(branding?.logo_light_url)
  const shouldRenderImage = !logoError && Boolean(resolvedLogoUrl)


  const primaryColor = branding?.primary_color || 'var(--primary)'
  const primaryHover = branding?.primary_hover || 'var(--primary-hover)'
  const primaryLight = branding?.primary_light || 'var(--primary-light)'
  const primaryGlow = branding?.primary_glow || 'rgba(193, 158, 115, 0.25)'

  const customColorsStyle = {
    '--primary': primaryColor,
    '--primary-hover': primaryHover,
    '--primary-light': primaryLight,
    '--primary-glow': primaryGlow,
    ...(branding?.background_color ? { '--bg': branding.background_color } : {}),
    ...(branding?.surface_color ? { '--surface': branding.surface_color } : {}),
    ...(branding?.surface_secondary ? { '--surface-2': branding.surface_secondary } : {}),
    ...(branding?.border_color ? { '--border': branding.border_color } : {}),
    ...(branding?.text_primary ? { '--text-1': branding.text_primary } : {}),
    ...(branding?.text_secondary ? { '--text-2': branding.text_secondary } : {}),
    ...(branding?.text_on_primary ? { '--text-on-primary': branding.text_on_primary } : {}),
  } as React.CSSProperties & Record<string, string | number | undefined>

  const handleWhatsApp = () => {
    if (!contactPhone) return
    const cleanPhone = contactPhone.replace(/\D/g, '')
    const msg = encodeURIComponent(
      `Olá! Gostaria de agendar um diagnóstico de BPO e controladoria financeira com a equipe da ${brandName}.`
    )
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-text-on-primary"
      style={customColorsStyle}
      data-testid="bpex-custom-landing"
    >
      {/* Navbar Exclusiva */}
      <header className="sticky top-0 z-50 bg-surface/90 backdrop-blur-md border-b border-border">
        <div className="max-w-[1360px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-h-[36px]">
            {shouldRenderImage ? (
              <img
                src={resolvedLogoUrl}
                alt={brandName}
                onError={() => setLogoError(true)}
                className="h-9 w-auto max-h-9 max-w-[220px] object-contain transition-opacity duration-200"
                loading="eager"
                decoding="async"
              />
            ) : (
              <span className="font-display text-lg font-bold text-text-1 tracking-tight select-none">{brandName}</span>
            )}
            <span className="hidden sm:inline-block px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[11px] font-semibold tracking-wide border border-primary/20">
              BPO &amp; Controladoria
            </span>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={loginUrl}
              className="text-xs font-semibold text-text-2 hover:text-text-1 transition-colors px-3 py-1.5"
            >
              Área do Cliente
            </a>
            <Button
              type="button"
              variant="primary"
              onClick={onOpenDemoModal}
              style={{ fontSize: '12px', padding: '7px 14px' }}
            >
              Agendar Diagnóstico
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Executivo Sob Medida */}
      <section className="py-20 lg:py-28 border-b border-border relative overflow-hidden">
        <div className="max-w-[1360px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/25 text-primary text-xs font-bold tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Controladoria &amp; BPO Financeiro de Alta Performance</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-display font-bold text-text-1 tracking-tight leading-[1.1]">
              Governança Contábil, Conciliação Diária e Inteligência Financeira
            </h1>

            <p className="text-base sm:text-lg text-text-2 font-normal leading-relaxed max-w-2xl">
              Terceirização financeira executiva com fechamento diário, relatórios em tempo real e auditoria contínua para empresas que buscam expansão segura e previsibilidade de caixa.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button
                type="button"
                variant="primary"
                onClick={onOpenDemoModal}
                icon={<ArrowRight size={14} />}
                style={{ fontSize: '13px', padding: '10px 20px' }}
              >
                Solicitar Diagnóstico Gratuito
              </Button>
              {contactPhone && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleWhatsApp}
                  style={{ fontSize: '13px', padding: '10px 20px' }}
                >
                  Falar no WhatsApp
                </Button>
              )}
            </div>

            {/* Garantias */}
            <div className="flex flex-wrap gap-6 pt-4 text-xs text-text-3 border-t border-border/70">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span>SLA de Conciliação D+0</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Lock className="w-4 h-4 text-primary" />
                <span>Sigilo e Conformidade LGPD</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-primary" />
                <span>Consultores Especialistas Dedicados</span>
              </div>
            </div>
          </div>

          {/* Painel Lateral de Indicadores */}
          <div className="lg:col-span-5">
            <div className="p-6 rounded-2xl border border-border bg-surface shadow-md space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <span className="text-xs font-bold uppercase tracking-wider text-text-3">Indicadores Auditados</span>
                <span className="text-[11px] font-mono font-semibold text-primary">LIVE METRICS</span>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-xl border border-border/80 bg-surface-2 flex items-center justify-between">
                  <div>
                    <span className="block text-[11px] text-text-3 font-medium">Volume Gerenciado / Ano</span>
                    <span className="text-2xl font-bold font-mono text-text-1">+R$ 480 Milhões</span>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-border/80 bg-surface-2 flex items-center justify-between">
                  <div>
                    <span className="block text-[11px] text-text-3 font-medium">Acurácia em Conciliações</span>
                    <span className="text-2xl font-bold font-mono text-text-1">99.85%</span>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-border/80 bg-surface-2 flex items-center justify-between">
                  <div>
                    <span className="block text-[11px] text-text-3 font-medium">Empresas e Grupos Atendidos</span>
                    <span className="text-2xl font-bold font-mono text-text-1">+220 Clientes</span>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Building2 className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Pilares de Governança */}
      <section className="py-20 bg-surface-2/60 border-b border-border">
        <div className="max-w-[1360px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-bold text-primary uppercase tracking-widest">Metodologia BPEX</span>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-text-1 mt-1">
              Como Transformamos a Gestão Financeira da sua Empresa
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-5 rounded-xl border border-border bg-surface space-y-3">
              <span className="text-xs font-mono font-bold text-primary">01</span>
              <h3 className="text-base font-bold text-text-1">Conciliação Bancária Diária</h3>
              <p className="text-xs text-text-3 leading-relaxed">
                Importação automática de extratos OFX/Open Finance e conferência diária de cada centavo.
              </p>
            </div>

            <div className="p-5 rounded-xl border border-border bg-surface space-y-3">
              <span className="text-xs font-mono font-bold text-primary">02</span>
              <h3 className="text-base font-bold text-text-1">Contas a Pagar e Receber</h3>
              <p className="text-xs text-text-3 leading-relaxed">
                Gestão rigorosa de cobranças, emissão de boletos, pix e agendamento de liquidações.
              </p>
            </div>

            <div className="p-5 rounded-xl border border-border bg-surface space-y-3">
              <span className="text-xs font-mono font-bold text-primary">03</span>
              <h3 className="text-base font-bold text-text-1">DRE Gerencial em Tempo Real</h3>
              <p className="text-xs text-text-3 leading-relaxed">
                Demonstrações de resultado por centro de custo e margem de contribuição atualizadas.
              </p>
            </div>

            <div className="p-5 rounded-xl border border-border bg-surface space-y-3">
              <span className="text-xs font-mono font-bold text-primary">04</span>
              <h3 className="text-base font-bold text-text-1">Controladoria e Advisory</h3>
              <p className="text-xs text-text-3 leading-relaxed">
                Reuniões mensais com consultores seniores para planejamento tributário e orçamentário.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rodapé Corporativo */}
      <footer className="py-10 bg-surface border-t border-border text-center text-xs text-text-3">
        <div className="max-w-[1360px] mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} {brandName}. Todos os direitos reservados.</p>
          <div className="flex items-center gap-4">
            {contactEmail && <span className="hover:text-text-1">{contactEmail}</span>}
            <a href={loginUrl} className="font-semibold text-primary hover:underline">Acesso Restrito</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
